import { lazy, ReactElement, Suspense } from 'react';
import { Navigate, Route, Routes as RouterRoutes } from 'react-router-dom';
import { Divider, Result } from 'antd';
import { EAppRoutes, Permission } from '@common/enums';
import { AuthContextProvider } from '@common/hooks/auth/AuthContextProvider';
import FullPageSpinnerLayout from '@common/layouts/FullPageSpinnerLayout';
import NavLayout from '@common/layouts/HeaderNavLayout';
import { AuthAndPermissionGuard } from '@features/auth/components/AuthAndPermissionGuard';
import AuthenticatedRoutes from '@features/auth/components/AuthenticatedRoutes';
import { BulkDealRouteWithContext } from '@features/deals/contexts';
import AccountSelection from '@features/navigation/components/AccountSelection';
import { AccountContextProvider } from '@features/navigation/hooks/useAccountContextProvider';
import { TargetingRouteWithContext } from '@features/targeting/contexts';
import { targetingRoutes } from './routes';
import { bulkDealRoutes } from './routes/bulk.deal.routes';

const UserLogoutLazy = lazy(() => import('@features/auth/components/UserLogout'));
const DashboardLazy = lazy(() => import('@pages/Home'));
const AdminUsersInviteLazy = lazy(() => import('@pages/AdminUsersInvite'));
const LoginLazy = lazy(() => import('@pages/Login'));
const DataAudienceLazy = lazy(() => import('@pages/DataAudience'));
const CreateLocationsGroupLazy = lazy(() => import('@pages/DataAudience/CreateLocationsGroup'));
const EditLocationsGroupLazy = lazy(() => import('@pages/DataAudience/EditLocationsGroup'));
const CreateCustomAudienceLazy = lazy(() => import('@pages/DataAudience/CreateCustomAudience'));
const UpdateCustomAudienceLazy = lazy(() => import('@pages/DataAudience/UpdateCustomAudience'));
const CreateCustomPixelLazy = lazy(() => import('@pages/DataAudience/CreateCustomPixel'));
const EditCustomPixelLazy = lazy(() => import('@pages/DataAudience/EditCustomPixel'));
const InventoryLazy = lazy(() => import('@pages/Inventory'));
const CreateInventoryLazy = lazy(() => import('@pages/Inventory/Create'));
const EditInventoryLazy = lazy(() => import('@pages/Inventory/Edit'));
const EditBulkInventoryLazy = lazy(() => import('@pages/Inventory/BulkEdit'));
const EditTargetingOptimizationLazy = lazy(() => import('@pages/Inventory/EditTargetingOptimization'));
const EditInventoryAudiencesDataLazy = lazy(() => import('@pages/Inventory/Edit/EditAudiencesData'));
const EditInventoryTargetingLazy = lazy(() => import('@pages/Inventory/Edit/EditTargeting'));
const EditInventoryDCOLazy = lazy(() => import('@pages/Inventory/Edit/EditDco'));

const headerActions = (
  <>
    <AccountSelection />
    <Divider type='vertical' style={{ margin: '0 24px', height: 32 }} />
  </>
);

const AppRoutes = (): ReactElement => {
  return (
    <Suspense fallback={<FullPageSpinnerLayout />}>
      <AuthContextProvider>
        <AccountContextProvider>
          <RouterRoutes>
            <Route element={<AuthenticatedRoutes />}>
              <Route path={EAppRoutes.Login} element={<LoginLazy />} />
              <Route path={'*'} element={<Result status='404' title='This section in progress' />} />
              <Route element={<NavLayout />}>
                <Route path={EAppRoutes.Logout} element={<UserLogoutLazy />} />
                <Route path={EAppRoutes.Home} element={<DashboardLazy />} />
                <Route path={EAppRoutes.AdminUsersInvite} element={<AdminUsersInviteLazy />} />
              </Route>
              <Route element={<NavLayout headerActions={headerActions} />}>
                <Route path={EAppRoutes.Inventory} element={<InventoryLazy />} />
                <Route path={EAppRoutes.DataAudience} element={<DataAudienceLazy />} />
              </Route>
              <Route path={EAppRoutes.CreateInventory} element={<CreateInventoryLazy />} />

              <Route element={<TargetingRouteWithContext />}>
                <Route element={<AuthAndPermissionGuard permission={Permission.AccessDealModule} />}>
                  <Route path={EAppRoutes.EditInventory} element={<EditInventoryLazy />} />
                  <Route path={EAppRoutes.EditInventoryAudiencesData} element={<EditInventoryAudiencesDataLazy />} />
                  <Route path={EAppRoutes.EditInventoryDCO} element={<EditInventoryDCOLazy />} />
                </Route>

                <Route path={EAppRoutes.EditInventoryTargeting} element={<EditInventoryTargetingLazy />}>
                  {targetingRoutes}
                </Route>
              </Route>

              <Route element={<BulkDealRouteWithContext />}>
                <Route path={EAppRoutes.TargetingBulkEditDeals} element={<EditBulkInventoryLazy />} />
                <Route path={EAppRoutes.TargetingBulkEditOptimization} element={<EditTargetingOptimizationLazy />}>
                  {bulkDealRoutes}
                </Route>
              </Route>

              <Route path={EAppRoutes.CreateLocationGroup} element={<CreateLocationsGroupLazy />} />
              <Route path={EAppRoutes.EditLocationGroup} element={<EditLocationsGroupLazy />} />
              <Route path={EAppRoutes.CreateCustomAudience} element={<CreateCustomAudienceLazy />} />
              <Route path={EAppRoutes.UpdateCustomAudience} element={<UpdateCustomAudienceLazy />} />
              <Route path={EAppRoutes.CreateCustomPixel} element={<CreateCustomPixelLazy />} />
              <Route path={EAppRoutes.EditCustomPixel} element={<EditCustomPixelLazy />} />
              <Route path='*' element={<Navigate to={EAppRoutes.Home} />} />
            </Route>
          </RouterRoutes>
        </AccountContextProvider>
      </AuthContextProvider>
    </Suspense>
  );
};

export default AppRoutes;
